import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  AddProductsModalComponent,
  AddProductsModalConfig,
  SelectableProduct,
} from '@app/sharedModule/product-selection/components/add-products-modal/add-products-modal.component';
import { FeatureAccessService } from '@app/services/feature-access/feature-access.service';
import { LockableTabItem } from '@app/services/feature-access/feature-access.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { Store } from '@ngrx/store';
import {
  CheckoutExtensionUI,
  DynamicReviewsExtension,
  ExtensionComponentType,
  ICheckoutExtensions,
  ProductReviews,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { ViewMode } from '../../../models';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { generateRandomString } from '@utils/string-utils';
import {
  DynamicReviewsTab,
  DynamicReviewsFormGroup,
  FallbackSettingsFormGroup,
  RatingBadgeConfigFormGroup,
  CarouselConfigFormGroup,
  ListConfigFormGroup,
  DesignOptionsFormGroup,
  ImportMethod,
  dynamicReviewsContentValidator,
  USAGE_TOKEN_PREFIX,
  USAGE_TOKEN_RANDOM_LENGTH,
  DEFAULT_INTERNAL_NAME,
  DEFAULT_INTERNAL_DESCRIPTION,
  DEFAULT_REVIEWS_TO_DISPLAY,
  DEFAULT_REVIEWS_PER_PAGE,
  DEFAULT_MIN_NUMBER_OF_REVIEWS,
  ExcludedProductItem,
} from './model';
import { TypedRuleGroup } from '@app/sharedModule/components/rules-dynamic-form/model';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import {
  getExtensionRules,
  PRODUCT_RELATED_RULE_ENTITIES,
} from '@pages/checkout-app/checkout-layout/main/components/extensions/shared/all-extension-rules';

@Component({
  selector: 'dynamic-reviews',
  templateUrl: './dynamic-reviews.component.html',
  styleUrls: ['./dynamic-reviews.component.scss'],
  standalone: false,
})
export class DynamicReviewsComponent implements OnInit {
  get fallbackSettingsGroup(): FormGroup<FallbackSettingsFormGroup> {
    return this.formGroup.controls.fallbackSettings;
  }

  get ratingBadgeConfigGroup(): FormGroup<RatingBadgeConfigFormGroup> {
    return this.formGroup.controls.ratingBadgeConfig;
  }

  get carouselConfigGroup(): FormGroup<CarouselConfigFormGroup> {
    return this.formGroup.controls.carouselConfig;
  }

  get listConfigGroup(): FormGroup<ListConfigFormGroup> {
    return this.formGroup.controls.listConfig;
  }

  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;
  tabs: LockableTabItem<DynamicReviewsTab>[] = [
    { label: 'Settings', value: DynamicReviewsTab.SETTINGS },
    { label: 'Appearance', value: DynamicReviewsTab.APPEARANCE },
    { label: 'Display Conditions', value: DynamicReviewsTab.DISPLAY_CONDITIONS },
  ];
  DynamicReviewsTab = DynamicReviewsTab;
  activeTab = DynamicReviewsTab.SETTINGS;
  formGroup: FormGroup<DynamicReviewsFormGroup>;
  displayRules = getExtensionRules();
  displayRulesEntities = this.displayRules.map((r) => r.entity);
  excludeProductsRules = getExtensionRules({ onlyRules: PRODUCT_RELATED_RULE_ENTITIES });
  excludeProductsEntities = this.excludeProductsRules.map((r) => r.entity);
  LayoutStyle = DynamicReviewsExtension.LayoutStyle;
  RatingBadgeType = DynamicReviewsExtension.RatingBadgeType;
  CarouselNavigationStyle = DynamicReviewsExtension.CarouselNavigationStyle;

  private destroyRef = inject(DestroyRef);

  constructor(
    private store: Store<CheckoutReducer.State>,
    private extensionsService: ExtensionsService,
    private rulesDynamicFormService: RulesDynamicFormService,
    private featureAccess: FeatureAccessService,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.tabs = this.featureAccess.lockTabOnFreePlan(this.tabs, DynamicReviewsTab.DISPLAY_CONDITIONS);
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.DYNAMIC_REVIEWS);

    this.initForm();

    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
    });

    this.formGroup.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.extensionsService.setFormValid(this.formGroup.valid);
    });

    this.extensionsService.setFormValid(this.formGroup.valid);

    this.loadedExtension$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.updateFormValues(extension.template, extension.usageToken);
      }
    });

    this.extensionsService.save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.handleSave();
    });
  }

  onAddExcludedProducts(): void {
    const current: ExcludedProductItem[] = this.formGroup.controls.excludeProducts.value ?? [];

    const config: AddProductsModalConfig = {
      title: 'Exclude Products From Reviews',
      searchPlaceholder: 'Search products by name or SKU...',
      addButtonText: 'Select',
      excludeProductIds: current.map((product) => product.id),
      fetchProducts: (query) =>
        firstValueFrom(this.extensionsService.queryShopifyForProductsUsingRestrictionRules({ query })),
    };

    this.dialog
      .open(AddProductsModalComponent, {
        data: config,
        panelClass: 'add-products-modal-container',
        width: '700px',
        maxHeight: '80vh',
      })
      .afterClosed()
      .subscribe((selected: SelectableProduct[] | undefined) => {
        if (!selected?.length) return;

        const added: ExcludedProductItem[] = selected.map((product) => ({
          id: product.id,
          name: product.title,
          description: product.description,
          imageUrl: product.imageUrl,
          sku: product.sku,
          price: product.price,
        }));

        this.formGroup.controls.excludeProducts.setValue([...current, ...added]);
        this.formGroup.markAsDirty();
      });
  }

  onRemoveExcludedProduct(productId: string | number): void {
    const current = this.formGroup.controls.excludeProducts.value ?? [];
    const remaining = current.filter((product) => String(product.id) !== String(productId));

    this.formGroup.controls.excludeProducts.setValue(remaining);
    this.formGroup.markAsDirty();
  }

  onTabChange(event: DynamicReviewsTab) {
    this.activeTab = event;
  }

  private initForm() {
    this.formGroup = new FormGroup<DynamicReviewsFormGroup>(
      {
        usageToken: new FormControl(USAGE_TOKEN_PREFIX + generateRandomString(USAGE_TOKEN_RANDOM_LENGTH), {
          nonNullable: true,
        }),
        internalName: new FormControl(DEFAULT_INTERNAL_NAME, {
          nonNullable: true,
          validators: [Validators.required],
        }),
        internalDescription: new FormControl(DEFAULT_INTERNAL_DESCRIPTION, { nonNullable: true }),
        importMethod: new FormControl<ImportMethod>('csv', { nonNullable: true }),
        reviewDataSource: new FormControl(DynamicReviewsExtension.ReviewDataSource.CSV, { nonNullable: true }),
        csvFileUrl: new FormControl('', { nonNullable: true }),
        csvFileName: new FormControl('', { nonNullable: true }),
        csvFileSize: new FormControl(0, { nonNullable: true }),
        reviewCount: new FormControl(0, { nonNullable: true }),
        pendingCsvContent: new FormControl('', { nonNullable: true }),
        fallbackSettings: this.createFallbackSettingsGroup(),
        minStarRatingEnabled: new FormControl(false, { nonNullable: true }),
        minStarRating: new FormControl(DynamicReviewsExtension.DEFAULT_MIN_STAR_RATING, { nonNullable: true }),
        reviewsToDisplay: new FormControl(DEFAULT_REVIEWS_TO_DISPLAY, { nonNullable: true }),
        layoutStyle: new FormControl(DynamicReviewsExtension.LayoutStyle.RATING_BADGE, { nonNullable: true }),
        ratingBadgeConfig: this.createRatingBadgeConfigGroup(),
        carouselConfig: this.createCarouselConfigGroup(),
        listConfig: this.createListConfigGroup(),
        displayRules: this.rulesDynamicFormService.initRulesFormArray([], this.displayRules) as FormArray<
          FormGroup<TypedRuleGroup>
        >,
        minNumberOfReviewsEnabled: new FormControl(false, { nonNullable: true }),
        minNumberOfReviews: new FormControl(DEFAULT_MIN_NUMBER_OF_REVIEWS, { nonNullable: true }),
        excludeProductsEnabled: new FormControl(false, { nonNullable: true }),
        excludeProductsMode: new FormControl(ProductReviews.ExclusionMode.MANUAL, { nonNullable: true }),
        excludeProducts: new FormControl<ExcludedProductItem[]>([], { nonNullable: true }),
        excludeProductsRules: this.rulesDynamicFormService.initRulesFormArray(
          [],
          this.excludeProductsRules,
        ) as FormArray<FormGroup<TypedRuleGroup>>,
      },
      { validators: [dynamicReviewsContentValidator] },
    );

    this.syncExcludeProductsRulesState();
    this.formGroup.controls.excludeProductsEnabled.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncExcludeProductsRulesState());
    this.formGroup.controls.excludeProductsMode.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncExcludeProductsRulesState());

    this.dispatchExampleViewValues(this.formGroup.value);
  }

  // A hidden half-filled rule would keep a required validator alive and disable Save.
  private syncExcludeProductsRulesState(): void {
    const usesSmartRules =
      this.formGroup.controls.excludeProductsEnabled.value &&
      this.formGroup.controls.excludeProductsMode.value === ProductReviews.ExclusionMode.SMART;
    const rules = this.formGroup.controls.excludeProductsRules;

    if (usesSmartRules && rules.disabled) {
      rules.enable({ emitEvent: false });
    } else if (!usesSmartRules && rules.enabled) {
      rules.disable({ emitEvent: false });
    }
  }

  private createFallbackSettingsGroup(
    settings?: DynamicReviewsExtension.FallbackSettings,
  ): FormGroup<FallbackSettingsFormGroup> {
    return new FormGroup<FallbackSettingsFormGroup>({
      enabled: new FormControl(settings?.enabled ?? false, { nonNullable: true }),
      text: new FormControl(settings?.text ?? '', { nonNullable: true }),
    });
  }

  private createRatingBadgeConfigGroup(
    config?: DynamicReviewsExtension.RatingBadgeConfig,
  ): FormGroup<RatingBadgeConfigFormGroup> {
    return new FormGroup<RatingBadgeConfigFormGroup>({
      type: new FormControl(config?.type ?? DynamicReviewsExtension.RatingBadgeType.STARS, { nonNullable: true }),
      starColor: new FormControl(config?.starColor ?? DynamicReviewsExtension.DEFAULT_STAR_COLOR, {
        nonNullable: true,
      }),
    });
  }

  private createDesignOptionsGroup(design?: DynamicReviewsExtension.DesignOptions): FormGroup<DesignOptionsFormGroup> {
    return new FormGroup<DesignOptionsFormGroup>({
      starColor: new FormControl(design?.starColor ?? DynamicReviewsExtension.DEFAULT_STAR_COLOR, {
        nonNullable: true,
      }),
      backgroundColor: new FormControl(design?.backgroundColor ?? 'transparent', { nonNullable: true }),
      border: new FormControl(design?.border ?? CheckoutExtensionUI.BorderType.NONE, { nonNullable: true }),
      borderWidth: new FormControl(design?.borderWidth ?? CheckoutExtensionUI.BorderWidth.BASE, { nonNullable: true }),
      cornerRadius: new FormControl(design?.cornerRadius ?? CheckoutExtensionUI.CornerRadius.NONE, {
        nonNullable: true,
      }),
      padding: new FormControl(design?.padding ?? CheckoutExtensionUI.Padding.BASE, { nonNullable: true }),
    });
  }

  private createCarouselConfigGroup(
    config?: DynamicReviewsExtension.CarouselConfig,
  ): FormGroup<CarouselConfigFormGroup> {
    return new FormGroup<CarouselConfigFormGroup>({
      navigationStyle: new FormControl(
        config?.navigationStyle ?? DynamicReviewsExtension.CarouselNavigationStyle.ARROWS,
        { nonNullable: true },
      ),
      starPosition: new FormControl(config?.starPosition ?? DynamicReviewsExtension.StarPosition.TOP, {
        nonNullable: true,
      }),
      title: new FormControl(config?.title ?? '', { nonNullable: true }),
      description: new FormControl(config?.description ?? '{review_text} {reviewer_name}', { nonNullable: true }),
      design: this.createDesignOptionsGroup(config?.design),
    });
  }

  private createListConfigGroup(config?: DynamicReviewsExtension.ListConfig): FormGroup<ListConfigFormGroup> {
    return new FormGroup<ListConfigFormGroup>({
      title: new FormControl(config?.title ?? '', { nonNullable: true }),
      description: new FormControl(config?.description ?? '{review_text} {reviewer_name}', { nonNullable: true }),
      design: this.createDesignOptionsGroup(config?.design),
      reviewsPerPage: new FormControl(config?.reviewsPerPage ?? DEFAULT_REVIEWS_PER_PAGE, { nonNullable: true }),
    });
  }

  private updateFormValues(loadedValues: DynamicReviewsExtension.Template, usageToken?: string) {
    const reviewDataSource = loadedValues.reviewDataSource ?? DynamicReviewsExtension.ReviewDataSource.CSV;
    const importMethod: ImportMethod =
      reviewDataSource === DynamicReviewsExtension.ReviewDataSource.CSV ? 'csv' : 'integration';

    this.formGroup.patchValue({
      usageToken: usageToken ?? this.formGroup.get('usageToken').value,
      internalName: loadedValues.internalName ?? DEFAULT_INTERNAL_NAME,
      importMethod,
      reviewDataSource,
      csvFileUrl: loadedValues.csvFileUrl ?? '',
      csvFileName: loadedValues.csvFileName ?? '',
      csvFileSize: loadedValues.csvFileSize ?? 0,
      reviewCount: loadedValues.reviewCount ?? 0,
      reviewsToDisplay: loadedValues.reviewsToDisplay ?? DEFAULT_REVIEWS_TO_DISPLAY,
      layoutStyle: loadedValues.layoutStyle ?? DynamicReviewsExtension.LayoutStyle.RATING_BADGE,
      minStarRatingEnabled: loadedValues.minStarRatingEnabled ?? false,
      minStarRating: loadedValues.minStarRating ?? DynamicReviewsExtension.DEFAULT_MIN_STAR_RATING,
      minNumberOfReviewsEnabled: loadedValues.minNumberOfReviewsEnabled ?? false,
      minNumberOfReviews: loadedValues.minNumberOfReviews ?? DEFAULT_MIN_NUMBER_OF_REVIEWS,
      excludeProductsEnabled: loadedValues.excludeProductsEnabled ?? false,
      excludeProductsMode: loadedValues.excludeProductsMode ?? ProductReviews.ExclusionMode.MANUAL,
      excludeProducts: (loadedValues.excludeProducts ?? []).map((product) => ({
        ...product,
        name: product.name ?? '',
      })),
    });

    this.formGroup.setControl('fallbackSettings', this.createFallbackSettingsGroup(loadedValues.fallbackSettings));
    this.formGroup.setControl('ratingBadgeConfig', this.createRatingBadgeConfigGroup(loadedValues.ratingBadgeConfig));
    this.formGroup.setControl('carouselConfig', this.createCarouselConfigGroup(loadedValues.carouselConfig));
    this.formGroup.setControl('listConfig', this.createListConfigGroup(loadedValues.listConfig));

    this.formGroup.setControl(
      'displayRules',
      this.rulesDynamicFormService.initRulesFormArray(
        loadedValues.restrictionsRules ?? [],
        this.displayRules,
      ) as FormArray<FormGroup<TypedRuleGroup>>,
    );

    this.formGroup.setControl(
      'excludeProductsRules',
      this.rulesDynamicFormService.initRulesFormArray(
        loadedValues.excludeProductsRules ?? [],
        this.excludeProductsRules,
      ) as FormArray<FormGroup<TypedRuleGroup>>,
    );

    this.syncExcludeProductsRulesState();
    this.dispatchExampleViewValues(this.formGroup.value);
  }

  private dispatchExampleViewValues(values: FormGroup<DynamicReviewsFormGroup>['value']) {
    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.DYNAMIC_REVIEWS]: {
        mode,
        template: values,
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  private handleSave() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const formValue = this.formGroup.getRawValue();
    const { usageToken, ...rest } = formValue;

    const template: DynamicReviewsExtension.Template = {
      internalName: rest.internalName,
      reviewDataSource: rest.reviewDataSource,
      csvFileUrl: rest.csvFileUrl,
      csvFileName: rest.csvFileName,
      csvFileSize: rest.csvFileSize,
      reviewCount: rest.reviewCount,
      pendingCsvContent: rest.pendingCsvContent || undefined,
      fallbackSettings: rest.fallbackSettings,
      minStarRatingEnabled: rest.minStarRatingEnabled,
      minStarRating: rest.minStarRating,
      reviewsToDisplay: rest.reviewsToDisplay,
      layoutStyle: rest.layoutStyle,
      ratingBadgeConfig: rest.ratingBadgeConfig,
      carouselConfig: rest.carouselConfig,
      listConfig: rest.listConfig,
      restrictionsRules: rest.displayRules as DynamicReviewsExtension.Template['restrictionsRules'],
      minNumberOfReviewsEnabled: rest.minNumberOfReviewsEnabled,
      minNumberOfReviews: rest.minNumberOfReviews,
      excludeProductsEnabled: rest.excludeProductsEnabled,
      excludeProductsMode: rest.excludeProductsMode,
      excludeProducts: rest.excludeProducts,
      excludeProductsRules: rest.excludeProductsRules as DynamicReviewsExtension.Template['excludeProductsRules'],
    };

    const payload: Partial<ICheckoutExtensions> = {
      template,
      usageToken,
      type: ExtensionComponentType.DYNAMIC_REVIEWS,
    };

    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: payload }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }
}
